import { deleteReapproCategory, useReapproCategories } from '@gozoki/api'
import { includesLowerCase } from '@gozoki/tools'
import { Button, TextField } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { useRights } from '../../../utils/hooks'
import { ConfirmResourceDeletionButton } from '../../communication/ConfirmDeletionButton'
import { useToast } from '../../communication/Toast'
import SimpleDataGrid from '../../containers/SimpleDataGrid'
import ReapproAlgorithmMatrixModal from './ReapproAlgorithmMatrixModal'
import ReapproAlgorithmParamsPanel from './ReapproAlgorithmParamsPanel'

const reapproCategoriesColDef: GridColDef[] = [
  { field: 'name', headerName: 'Nom', width: 200 },
  { field: 'daysBefore', headerName: 'J-x', width: 50 },
  {
    field: 'computeHour',
    headerName: 'Heure de calcul',
    width: 140,
    renderCell: ({ value }) => value.split(':').slice(0, 2).join(':'),
  },
  {
    field: 'sendHour',
    headerName: "Heure d'envoi",
    width: 140,
    renderCell: ({ value }) => value.split(':').slice(0, 2).join(':'),
  },
  {
    field: 'logHour',
    headerName: 'Heure de log',
    width: 140,
    renderCell: ({ value }) => value.split(':').slice(0, 2).join(':'),
  },
]

export const ReapproCategoriesPanel = () => {
  const navigate = useNavigate({ from: '/categories/' })
  const isUserAdmin = useRights(['ADMIN'])
  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [selectedCategories, setSelectedCategories] = useState<number[]>([])

  const [isAlgorithmModalOpen, setIsAlgorithmModalOpen] = useState(false)

  const {
    data: reapproCategories = [],
    refetch,
    isLoading,
    isRefetching,
    isPending,
  } = useReapproCategories()
  // Transform fetched categories into DataGrid format
  const reapproCategoriesRows = reapproCategories.filter((category) =>
    includesLowerCase(category.name, search),
  )

  const deleteCategoryMut = useMutation({ mutationFn: deleteReapproCategory })
  const handleDeleteCategory = () => {
    const deletionPromise = deleteCategoryMut.mutateAsync(selectedCategories, {
      onSuccess: () => {
        refetch()
      },
      onError: (error) => {
        let message = 'Une erreur inconnue est survenue lors de la suppression de la catégorie'
        if (
          error instanceof AxiosError &&
          error.response?.data &&
          typeof error.response.data === 'object' &&
          'message' in error.response.data
        ) {
          message = error.response.data.message
        }
        showToast({
          message,
          severity: 'error',
        })
      },
    })
    setSelectedCategories([]) // Avoid disabled button flickering
    return deletionPromise
  }

  const navigateAndUpdateCategory = () => {
    navigate({
      to: '/categories/reappro/new',
      search: { reapproCategoryId: selectedCategories[0] },
    })
  }

  const categoriesDisabled = selectedCategories.length === 0 || deleteCategoryMut.isPending
  return (
    <>
      <Toast />
      <div style={{ display: 'flex', alignItems: 'center', padding: '16px' }}>
        <TextField
          variant="outlined"
          label="Recherche"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div style={{ flexGrow: 1 }} />
        <ConfirmResourceDeletionButton
          handleResourceDeletion={handleDeleteCategory}
          disabled={!isUserAdmin ? true : categoriesDisabled}
        />
        <Button
          variant="contained"
          sx={{ height: '36px' }}
          disabled={categoriesDisabled}
          onClick={navigateAndUpdateCategory}
        >
          {isUserAdmin ? 'Modifier' : 'Voir'}
        </Button>
        <Button
          variant="outlined"
          sx={{ height: '36px', marginLeft: '12px' }}
          disabled={!isUserAdmin}
          onClick={() => setIsAlgorithmModalOpen(true)}
        >
          Algorithmes réappro
        </Button>
      </div>
      <SimpleDataGrid
        rowHeight={70} // For image display
        columns={reapproCategoriesColDef}
        rows={reapproCategoriesRows}
        loading={isLoading || isPending || isRefetching}
        onRowSelectionModelChange={(newSelection) =>
          setSelectedCategories(newSelection as number[])
        }
      />
      <ReapproAlgorithmMatrixModal
        open={isAlgorithmModalOpen}
        onClose={() => setIsAlgorithmModalOpen(false)}
        isLoading={isLoading || isPending || isRefetching}
        reapproCategories={reapproCategories}
      />
      <ReapproAlgorithmParamsPanel isUserAdmin={isUserAdmin} />
    </>
  )
}
